import { GridRowsProp } from '@mui/x-data-grid';

import { VALID_CREATOR_REGEX, VALID_BULK_ACCOUNTS } from '@/constants/regex';
import { Creator } from '@/models/creator';

export const isCreatorInvalid = (row: GridRowsProp[number]) => {
  const creator = String(row.creator ?? '').trim();
  if (!creator) return true;
  return !VALID_CREATOR_REGEX.test(creator);
};

export const isAccountInvalid = (row: GridRowsProp[number]) => {
  if (row.isDuplicateAccount) return true;
  const account = String(row.account ?? '')
    .trim()
    .toLocaleLowerCase();
  return !VALID_BULK_ACCOUNTS.some((re) => re.test(account));
};

export const isRowInvalid = (row: GridRowsProp[number]) =>
  isCreatorInvalid(row) || isAccountInvalid(row);

export const buildInitialRows = (
  creators: Creator.FileValidationCreator[]
): GridRowsProp =>
  creators
    .flatMap((creator) => {
      const {
        wrong = [],
        duplicates = [],
        hasInvalidCreatorName
      } = creator.creatorWarnings ?? {};
      const hasInvalidName = !creator.name.trim() || hasInvalidCreatorName;
      const accountEntries = hasInvalidName
        ? (creator.accounts ?? []).map((account) => ({ account }))
        : [];

      const entries: Array<{ account: string; isDuplicateAccount?: true }> = [
        ...wrong.map((account) => ({ account })),
        ...duplicates.map((account) => ({ account, isDuplicateAccount: true })),
        ...accountEntries
      ];

      return entries.map((entry) => ({ creator, entry }));
    })
    .map(({ creator, entry }, i) => ({
      id: `${i}-${creator.name.replace(/ /g, '_')}`,
      creator: creator.name,
      account: entry.account,
      ...(entry.isDuplicateAccount && { isDuplicateAccount: true })
    }));

export const buildPayload = (
  original: Creator.FileValidationCreator[],
  editedRows: GridRowsProp
): Creator.BulkCreatePayload => {
  const clean = original
    .filter(
      (c) =>
        c.name.trim() &&
        !c.creatorWarnings?.wrong.length &&
        !c.creatorWarnings?.duplicates.length &&
        !c.creatorWarnings?.hasInvalidCreatorName
    )
    .map((c) => ({ name: c.name, accounts: c.accounts ?? [] }));

  const fromGrid = editedRows
    .filter((r) => !isRowInvalid(r))
    .reduce<Record<string, string[]>>((acc, row) => {
      const name = (row.creator as string).trim();
      const account = (row.account as string).trim();
      return {
        ...acc,
        [name]: [...(acc[name] ?? []), account]
      };
    }, {});

  const gridCreators = Object.entries(fromGrid).map(([name, accounts]) => ({
    name,
    accounts
  }));

  return { creators: [...clean, ...gridCreators] };
};
